import React, { useState, useEffect, useRef } from "react"
import { useLocation, useHistory } from "react-router-dom"
import ReactMarkdown from "react-markdown"

function formatChangeLogToMarkdown(title, logs) {
    const groupedLogs = {};
    for (const log of logs) {
        if (!groupedLogs[log.type]) {
            groupedLogs[log.type] = [];
        }
        const content = log.content.trim();
        if (!groupedLogs[log.type].includes(content)) {
            groupedLogs[log.type].push(content);
        }
    }
    let markdown = `### ${title}\n\n`;
    const sortedTypes = Object.keys(groupedLogs).sort();
    for (const type of sortedTypes) {
        markdown += `### ${type}\n\n`;
        for (const content of groupedLogs[type]) {
            markdown += `- ${content}\n`;
        }
        markdown += '\n';
    }
    return markdown;
}

function parseMajorMinorVersion(version) {
    const match = version.match(/^(\d+)\.(\d+)/);
    if (match) {
        return [parseInt(match[1]), parseInt(match[2])];
    }
    throw new Error('Invalid version format: ' + version);
}

const DownloadChangelog = () => {
    const location = useLocation()
    const history = useHistory()
    const [startVersion, setStartVersion] = useState("")
    const [endVersion, setEndVersion] = useState("")
    const [changelog, setChangelog] = useState(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    const fetchedRef = useRef(false)

    useEffect(() => {
        if (fetchedRef.current) return
        const params = new URLSearchParams(location.search)
        const urlStart = params.get("start")
        const urlEnd = params.get("end")
        if (urlEnd) {
            fetchedRef.current = true
            setEndVersion(urlEnd)
            if (urlStart) {
                setStartVersion(urlStart)
                fetchChangelog(urlStart, urlEnd)
            } else {
                try {
                    const [major, minor] = parseMajorMinorVersion(urlEnd)
                    const computedStart = `${major}.${minor - 1}.x`
                    setStartVersion(computedStart)
                    fetchChangelog(computedStart, urlEnd)
                } catch {
                    setError("Invalid end version format")
                }
            }
        }
    }, [])

    const fetchChangelog = (start, end) => {
        setLoading(true)
        setError(null)
        fetch(`/download/dreameditor/changelog?start=${start}&end=${end}`)
            .then(response => response.json())
            .then(data => {
                setChangelog(data.changelog)
                setLoading(false)
            })
            .catch(() => {
                setError("Failed to fetch changelog")
                setLoading(false)
            })
    }

    const handleFetchChangelog = () => {
        if (!endVersion) {
            setError("Please enter an end version")
            return
        }
        let start = startVersion;
        if (!startVersion) {
            const [major, minor] = parseMajorMinorVersion(endVersion)
            start = `${major}.${minor - 1}.x`
            setStartVersion(start)
        }
        history.push(`?start=${start}&end=${endVersion}`)
        fetchChangelog(start, endVersion)
    }

    return (
        <div className="mainContent">
            <div className="downloadChangelog">
                <h1>DreamEditor Changelog</h1>
                <div className="versionInputs">
                    <input
                        type="text"
                        placeholder="Start Version"
                        value={startVersion}
                        onChange={(e) => setStartVersion(e.target.value)}
                    />
                    <input
                        type="text"
                        placeholder="End Version"
                        value={endVersion}
                        onChange={(e) => setEndVersion(e.target.value)}
                    />
                    <button className="downloadBtn" onClick={handleFetchChangelog} disabled={loading}>
                        {loading ? "Loading..." : "Get Changelog"}
                    </button>
                </div>
                {error && <p className="error">{error}</p>}
                {changelog && (
                    <div className="changelogContent">
                        <ReactMarkdown>{formatChangeLogToMarkdown(`Changelog from ${startVersion} to ${endVersion}`, changelog)}</ReactMarkdown>
                    </div>
                )}
            </div>
        </div>
    )
}

export default DownloadChangelog